import React, { useEffect, useRef, useState } from "react";
import { MessageSquarePlus, X, CheckCircle2, Send } from "lucide-react";
import { useLocation } from "react-router-dom";
import TextArea from "../primitives/TextArea";
import TextButton from "../primitives/TextButton";
import { sendFeedback, assistantStatus, ApiError } from "../../services/api";
import AssistantChat from "./AssistantChat";
import "./FeedbackWidget.css";

const RESET_DELAY_MS = 2200;

function FeedbackWidget() {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [errMsg, setErrMsg] = useState(null);
    const [tab, setTab] = useState("feedback"); // feedback | assistant
    // Null until we've asked. The chat tab only appears once the backend
    // confirms an API key is configured, so we never offer a dead feature.
    const [assistantOn, setAssistantOn] = useState(false);
    const location = useLocation();
    const panelRef = useRef(null);
    const resetTimer = useRef(null);

    useEffect(() => () => clearTimeout(resetTimer.current), []);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        assistantStatus()
            .then((res) => {
                if (!cancelled) setAssistantOn(!!res?.available);
            })
            .catch(() => {
                if (!cancelled) setAssistantOn(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const submit = async () => {
        const trimmed = message.trim();
        if (!trimmed || status === "sending") return;
        setStatus("sending");
        setErrMsg(null);
        try {
            await sendFeedback({ message: trimmed, page: location.pathname });
            setStatus("sent");
            setMessage("");
            resetTimer.current = setTimeout(() => {
                setStatus("idle");
                setOpen(false);
            }, RESET_DELAY_MS);
        } catch (err) {
            setStatus("error");
            setErrMsg(
                err instanceof ApiError
                    ? err.message
                    : "Could not send feedback. Please try again."
            );
        }
    };

    return (
        <div className="fbw">
            {open && (
                <div
                    ref={panelRef}
                    className="fbw__panel"
                    role="dialog"
                    aria-label="Send feedback"
                >
                    <div className="fbw__head">
                        <span className="uppercase-label">
                            {assistantOn && tab === "assistant"
                                ? "Assistant"
                                : "Feedback"}
                        </span>
                        <button
                            type="button"
                            className="fbw__close focus-ring"
                            onClick={() => setOpen(false)}
                            aria-label="Close feedback"
                        >
                            <X size={14} aria-hidden="true" />
                        </button>
                    </div>

                    {assistantOn && (
                        <div className="fbw__tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "feedback"}
                                className={`fbw__tab ${tab === "feedback" ? "is-active" : ""}`}
                                onClick={() => setTab("feedback")}
                            >
                                Feedback
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={tab === "assistant"}
                                className={`fbw__tab ${tab === "assistant" ? "is-active" : ""}`}
                                onClick={() => setTab("assistant")}
                            >
                                Ask AI
                            </button>
                        </div>
                    )}

                    {assistantOn && tab === "assistant" ? (
                        <AssistantChat />
                    ) : status === "sent" ? (
                        <div className="fbw__sent" role="status">
                            <CheckCircle2 size={20} aria-hidden="true" />
                            <span>Thanks — sent!</span>
                        </div>
                    ) : (
                        <>
                            <p className="fbw__lede">
                                Found a bug or have an idea? It goes straight to
                                the team.
                            </p>
                            <TextArea
                                label="Your message"
                                value={message}
                                onChange={setMessage}
                                rows={4}
                                maxLength={4000}
                                placeholder="What's on your mind?"
                                error={status === "error" ? errMsg : undefined}
                            />
                            <TextButton
                                variant="primary"
                                icon={Send}
                                fullWidth
                                loading={status === "sending"}
                                disabled={!message.trim()}
                                onClick={submit}
                            >
                                Send feedback
                            </TextButton>
                        </>
                    )}
                </div>
            )}

            <button
                type="button"
                className="fbw__fab focus-ring"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-label="Send feedback"
                title="Send feedback"
            >
                <MessageSquarePlus size={20} aria-hidden="true" />
            </button>
        </div>
    );
}

export default FeedbackWidget;
